<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-management-team">
    <div class="manage-team-header">
      <div class="profile-label">Profiles:</div>
      <el-button class="wy-blue-button add-member" v-if="isAdminUser" ref="addMemberBtn" size="mini" @click="handleAddMember">{{ $t('button.addMember') }}</el-button>
    </div>
    <div class="wy-management-body">
      <div class="user-list" v-loading="loading">
        <div v-if="users && users.length > 0">
          <div class="user-section" v-for="user in users" :label="user.name" :key="user.id">
            <div class="div-user-name">{{user.firstName + ' ' + user.lastName}}</div>
            <div class="div-user-email">
              <div class="user-email-text" :title="user.email">{{user.email}}</div>
              <div class="user-type" v-if="user.userType=='REGULAR'">Internal User</div>
              <div class="user-type" v-else-if="user.userType=='CLOUD'">Cloud User</div>
              <div class="user-type" v-else></div>
            
              <div class="user-status" v-if="user && (user.status == 'S' || user.status == 'P')" >{{formatUserStatus(user.status)}}</div>

              <el-select v-if="user && (user.status == 'I' || user.status == 'A')"
                v-model="user.status"
                @change="handleStatusChanged(user)"
                collapse-tags
                slot="append"
                size="mini"
                style="margin-left: 5px;"
                popper-class="role-dropdown"
                :class="{'status-dropdown-class': true}"
                placeholder="Select Status">
                <el-option value="A" label="Active">Active</el-option>
                <el-option value="I" label="Inactive">Inactive</el-option>
              </el-select>

              <el-select v-if="authUser.company.companyCode==1"
                v-model="user.defaultCompanyId"
                @change="handleDefaultCompanyChanged(user)"
                collapse-tags
                slot="append"
                size="mini"
                style="margin-left: 5px;"
                popper-class="role-dropdown"
                :class="{'company-dropdown-class': true, 'float-right': noPendingUser}"
                placeholder="Default Company">
                <el-option
                  v-for="(item) in accessibleCompanies"
                  :key="item.id"
                  :label="item.name"
                  :value="item.id">
                </el-option>
              </el-select>

              <el-select
                v-model="user.role"
                @change="handleRoleChanged(user)"
                collapse-tags
                slot="append"
                size="mini"
                style="margin-left: 5px;"
                popper-class="role-dropdown"
                :class="{'role-dropdown-class': true, 'float-right': noPendingUser}"
                placeholder="Select Role">
                <el-option
                  v-for="(item) in appRoles"
                  :key="item.key"
                  :label="item.label.replace('Administrator', 'Admin')"
                  :value="item.key">
                </el-option>
              </el-select>
              
              <el-button class="wy-blue-button edit" size="mini" @click="handleShowDelete(user)">{{ $t('button.delete') }}</el-button>
              <el-button class="wy-blue-button edit" size="mini" @click="handleEdit(user)" :disabled="user.role == 'SYSTEM_ADMIN'">{{ $t('button.edit') }}</el-button>
              <el-button v-if="user.status=='P'" class="wy-blue-button re-send-email" size="mini" @click="handleResendEmail(user)">{{ $t('button.reSendEmail') }}</el-button>
            </div>
          </div>
        </div>
        <div v-else>No users</div>
      </div>
    </div>

    <el-dialog
      :title="title"
      v-if="showUserSteps"
      :visible.sync="showUserSteps"
      :show-close="false"
      :closeOnClickModal="false"
      append-to-body
      class="wys-dialog-box user-steps-dialog"
      width="920px">
      <wy-management-team-create-user :controller="module"></wy-management-team-create-user>
    </el-dialog>

    <el-dialog
      ref="editUserDialog"
      :title="title"
      v-if="showUserEdit"
      :visible.sync="showUserEdit"
      :show-close="false"
      :closeOnClickModal="false"
      append-to-body
      class="wys-dialog-box user-eidt-dialog"
      width="1050px">
      <wy-management-team-edit-user :controller="module"></wy-management-team-edit-user>
    </el-dialog>

    <el-dialog
      title=""
      v-if="dialogDeleteUserVisible"
      :visible.sync="dialogDeleteUserVisible"
      :close-on-click-modal="false"
      :show-close="false"
      :close-on-press-escape="false"
      append-to-body
      custom-class="black-list-dialog"
      :before-close="handleDeleteUserClose">
      <div class="div-black-list-title">Are you sure you want to delete this user?</div>
      <div slot="footer" class="dialog-footer">
        <el-button class="btn-cancel" @click="handleDeleteUserClose">Cancel</el-button>
        <el-button class="btn-confirm" type="primary" @click="handleDelete(userId)">Delete</el-button>
        <br clear="all"/>
      </div>
    </el-dialog>
  </div>
</template>

<script>
  import Vue from 'vue';
  import { mapGetters, mapActions } from 'vuex';
  import WyManagementTeamCreateUser from './wy-management-team-create-user';
  import WyManagementTeamEditUser from './wy-management-team-edit-user';

  export default Vue.extend({
    name: 'wy-management-team',
    components: {
      "wy-management-team-create-user": WyManagementTeamCreateUser,
      "wy-management-team-edit-user": WyManagementTeamEditUser
    },
    data() {
      return {
        module: this,
        loading: false,
        users: null,
        permissionList: null,
        accessibleCompanies: null,
        selectedUser: null,
        selectedRole: null,
        title: '',
        showUserEdit: false,
        showUserSteps: false,
        noPendingUser: true,
        dialogDeleteUserVisible: false,
        userId: null,
      }
    },

    computed: {
      ...mapGetters(['authUser', 'isAdminUser', 'appRoles', 'accessHost']),
    },

    methods: {
      ...mapActions(['getUsers', 'getAccessibleCompanies', 'getPermissions', 'updateUserRole', 'resendInvitationEmail', 'updateUserStatus', 'updateUserType', 'updateDefaultCompany', 'deleteUser']),

      // defaultCompanyList(user) {
      //   return user.role == 'SYSTEM_ADMIN' ? this.accessibleCompanies : this.accessibleCompanies.filter(item => user.permissionMap[item.id].length>0);
      // },

      handleDeleteUserClose() {
        this.dialogDeleteUserVisible = false;
      },

      handleAddMember() {
        this.showUserSteps = true;
      },

      formatUserStatus(key) {
        key = key.toUpperCase();
        let map = {
          "P": "Pending",
          "A": "Active",
          "S": "Suspended",
          "I": "Inactive"    
        }    
        if(map[key]) {
          return map[key];
        }
        return key;
      },

      async loadUsers() {
        let resPermissions = await this.getPermissions();
        let resCompanies = await this.getAccessibleCompanies();
        if(resPermissions.status == 200 && resCompanies.status == 200) {
          // if current company is system company, we show all companies
          // else we shows only companies under the same company account
          if(!this.authUser.company.isSystemCompany) {
            resCompanies.data = resCompanies.data.filter(el => el.accountId==this.authUser.company.accountId)
          }
          this.permissionList = resPermissions.data;
          this.accessibleCompanies = resCompanies.data;
        }
        
        let res = await this.getUsers(this.authUser.company.accountId);
        if(res && res.status == 200 && res.data){
          if(res.data.length > 0) {
            res.data.forEach(user => {
              // if(user.id == this.authUser.user.id) {
              //   user.disabled = true;
              // }

              if(user.status == 'P') {
                this.noPendingUser = false;
              }

              this.accessibleCompanies.forEach(company => {
                let isFound = user.accessList.find(el => el.companyId==company.id);
                if(isFound == null) {
                  user.accessList.push({
                    companyId: company.id,
                    permissions: []
                  })
                }
              });

              user.permissionMap = {};
              user.accessList.forEach(access => {
                user.permissionMap[access.companyId] = access.permissions;
              });
            })
            this.users = res.data.filter(user => !user.disabled);
          }
        }
      },

      async handleEdit(user) {
        this.selectedUser = user;
        this.showUserEdit = true;
      },

      async handleResendEmail(user) {
        let payload = {
          "email" : user.email,
          "fullUrl" : `${this.accessHost(user.userType)}/studio/registration`,
          "accessList": user.accessList
        }
        this.loading = true;
        let res = await this.resendInvitationEmail(payload)
        if(res.data && res.data.errorCode) {
          this.$root.showNotificationLocale("error", null, `errorCode.code${res.data.errorCode}`);
        }
        else if(res.status != 200) {
          this.$root.showNotificationLocale("error", 'error.serverErrorTitle', 'error.serverErrorMsg');
        }
        setTimeout(() => {
          this.loading = false;
        }, 500)
      },

      async handleRoleChanged(user) {
        let data = {};
        data.payload = {
          role: user.role,
          accessList: []
        }
        
        if(user.accessList != null) {
          // override the access permissions by the default product permissions
          user.accessList.forEach(access => {
            let newPermissions = [];
            if(user.role == "SYSTEM_ADMIN" || user.role == "ACCOUNT_ADMIN") {
              // if new role is admin, no need to update permission
              newPermissions = access.permissions;
            }
            else if(user.role == "USER") {
              // if new role is user, add read and write permisison to the exiting accessiable products
              if(access.permissions.includes(4000) || access.permissions.includes(4001)) {
                newPermissions.push(4000);
                newPermissions.push(4001)
              }
              if(access.permissions.includes(4100) || access.permissions.includes(4101)) {
                newPermissions.push(4100);
                newPermissions.push(4101)
              }
              if(access.permissions.includes(4200) || access.permissions.includes(4201)) {
                newPermissions.push(4200);
                newPermissions.push(4201)
              }
              if(access.permissions.includes(4300) || access.permissions.includes(4301)) {
                newPermissions.push(4300);
                newPermissions.push(4301)
              }
              if(access.permissions.includes(4400) || access.permissions.includes(4401)) {
                newPermissions.push(4400);
                newPermissions.push(4401)
              }
            }
            else if(user.role == "USER_READ_ONLY") {
              // if new role is user, add read only permisison to the exiting accessiable products
              if(access.permissions.includes(4000) || access.permissions.includes(4001)) {
                newPermissions.push(4000);
              }
              if(access.permissions.includes(4100) || access.permissions.includes(4101)) {
                newPermissions.push(4100);
              }
              if(access.permissions.includes(4200) || access.permissions.includes(4201)) {
                newPermissions.push(4200);
              }
              if(access.permissions.includes(4300) || access.permissions.includes(4301)) {
                newPermissions.push(4300);
              }
              if(access.permissions.includes(4400) || access.permissions.includes(4401)) {
                newPermissions.push(4400);
              }
            }

            data.payload.accessList.push({
              companyId: access.companyId,
              permissions: newPermissions
            });
          })
        }

        data.userId = user.id;
        let res = await this.updateUserRole(data);
        if(res.status === 200) {
          user.permissionMap = {};
          user.accessList = data.payload.accessList;
          user.accessList.forEach(access => {
            user.permissionMap[access.companyId] = access.permissions;
          });
          this.$root.showNotificationLocale("success", "account.alert.updateRoleSuccessTitle", "account.alert.updateRoleSuccessMsg");
        }
        else if(res.data && res.data.errorCode) {
          this.$root.showNotificationLocale("error", null,  `errorCode.code${res.data.errorCode}`);
        }
        else {
          this.$root.showNotificationLocale("error", null, "account.alert.updateRoleFailedTitle", "account.alert.updateRoleFailedMsg");
        }
      },

      async handleStatusChanged(user) {
        let data = {};
        data.payload = {
          id: user.id,
          status: user.status
        }
        
        data.userId = user.id;
        let res = await this.updateUserStatus(data);
        if(res.status === 200) {
          this.$root.showNotificationLocale("success", "account.alert.updateStatusSuccessTitle", "account.alert.updateStatusSuccessMsg");
        }
        else if(res.data && res.data.errorCode) {
          this.$root.showNotificationLocale("error", null,  `errorCode.code${res.data.errorCode}`);
        }
        else {
          this.$root.showNotificationLocale("error", null, "account.alert.updateStatusFailedTitle", "account.alert.updateStatusFailedMsg");
        }
      },

      async handleUserTypeChanged(user) {
        let data = {};
        data.payload = {
          id: user.id,
          userType: user.userType
        }
        
        data.userId = user.id;
        let res = await this.updateUserType(data);
        if(res.status === 200) {
          this.$root.showNotificationLocale("success", "account.alert.updateUserTypeSuccessTitle", "account.alert.updateUserTypeSuccessMsg");
        }
        else if(res.data && res.data.errorCode) {
          this.$root.showNotificationLocale("error", null,  `errorCode.code${res.data.errorCode}`);
        }
        else {
          this.$root.showNotificationLocale("error", null, "account.alert.updateUserTypeFailedTitle", "account.alert.updateUserTypeFailedMsg");
        }
      },

      async handleDefaultCompanyChanged(user) {
        let data = {};
        data.payload = {
          id: user.id,
          defaultCompanyId: user.defaultCompanyId
        }

        data.userId = user.id;
        let res = await this.updateDefaultCompany(data);
        if(res.status === 200) {
          this.$root.showNotificationLocale("success", "account.alert.updateDefaultCompanySuccessTitle", "account.alert.updateDefaultCompanySuccessMsg", {width: 600, duration: 10000});
          if(user.role != "SYSTEM_ADMIN") {
            this.handleEdit(user);
          }          
        }
        else if(res.data && res.data.errorCode) {
          this.$root.showNotificationLocale("error", null,  `errorCode.code${res.data.errorCode}`);
        }
        else {
          this.$root.showNotificationLocale("error", null, "account.alert.updateDefaultCompanyFailedTitle");
        }
      },

      async handleShowDelete(user){
        this.dialogDeleteUserVisible = true;
        this.userId = user.id;
      },

      async handleDelete(userId) {
        this.handleDeleteUserClose();
        let data = {};
        data.userId = userId;
        let res = await this.deleteUser(data);
        if(res.status === 200) {
          this.users = this.users.filter(eachUser => (eachUser.id != userId));
          this.$root.showNotificationLocale("success", "account.alert.deleteUserSuccessTitle", "account.alert.deleteUserSuccessMsg");
          this.$store.dispatch('amplitudeLog', {name: 'user_deleted', data: {user_id: userId}});
        }
        else if(res.data && res.data.errorCode) {
          this.$root.showNotificationLocale("error", null,  `errorCode.code${res.data.errorCode}`);
        }
        else {
          this.$root.showNotificationLocale("error", null, "account.alert.deleteUserFailedTitle", "account.alert.deleteUserFailedMsg");
        }

      }


    },

    created() {
      this.loadUsers();
    }
  });
</script>

<style lang="scss">
  .wy-app-modal .wy-app-modal-content {
    width:1350px !important;
  }
  .wy-management .el-tabs {
    *{
      font-size: 14px !important;
    }
  }
.wy-management-team {
  overflow-x: auto;
  overflow-y: hidden;
  height: 100%;
  padding-right: 15px;

  .manage-team-header {
    width: 100%;
    padding: 0px 0px 28px 0px;
    font-size: 16px;
    font-weight: 500;
    // min-width: 785px;
    min-width: 555px;

    .profile-label {
      // width: 46%;
      // width: 551px;
      display: inline-block;
    }
    .add-member {
      padding: 7px 15px 7px 15px;
    }
    .el-button.add-member {
      float: right;
    }
    .el-button.add-member:focus {
      outline: none;
      box-shadow: 0px 0px 10px #0a21f3a3;
    }
  }
  .wy-management-body {
    height: calc(100% - 40px);
    overflow-y: auto;
  }
  .user-list {
    width: fit-content;
    padding-right: 20px;
    //width: 790px;
  }
  .el-input-group {
    width: 54%;
  }
  .el-input-group__append {
    width: 120px;
  }
  .el-input-group__append, .el-input-group__prepend {
    background-color: #FFF;
    border: 1px solid #FFF;
  }
  
  .user-section {
    padding: 0px;
    margin-bottom: 10px;
    .div-user-name {
      font-size: 14px;
      font-weight: 500;
    }
    .div-user-email {
      padding-top: 0px;
      border-bottom: 1px solid #D7D7D8;
      padding-bottom: 5px;
      div.user-email-text {
        padding: 0px 0 0px 0;
        color: #004A87;
        font-size: 14px;
        display: inline-block;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        vertical-align: text-bottom;
        width: 306px;
        cursor: default;
      }
      div.user-type {
        padding: 0px 0 0px 0;
        font-size: 14px;
        display: inline-block;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        vertical-align: text-bottom;
        width: 100px;
        cursor: default;
      }
      .user-role {
        display: inline-flex;
        padding: 0 20px;
        font-size: 13px;
        width: 130px;
      }
      .user-status {
        display: inline-flex;
        padding: 0 20px;
        font-size: 13px;
        padding-left: 5px;
        padding-right: 10px;
        width: 18px;
        text-align: left;
        margin-left: 5px;
        margin-right: 52px;
      }
      .el-input--mini {
        font-size: 13px;
      }
      .el-input__suffix{
        right: -9px !important;
      }
      .role-dropdown-class.float-right {
        //float: right;
        //bottom: 5px;
        width: 120px !important;
        padding-right: 15px !important;
        margin-right: 20px !important;
      }
      .el-select.el-select--mini {
        padding-right: 15px;
        margin-right: 15px;
        input.el-input__inner {
          border-width: 0px 0px 0px 0px !important;
        }
      }
      .status-dropdown-class.el-select.el-select--mini {
        width: 60px;
        .el-input {
          width: 70px;
        }
      }
      .company-dropdown-class.el-select.el-select--mini {
        width: 140px;
        .el-input {
          width: 150px;
        }
      }
      .role-dropdown-class.el-select.el-select--mini {
        width: 100px;
        .el-input {
          width: 110px;
        }
      }
      .re-send-email, .edit {
        padding: 5px 10px 5px 10px;
        font-size: 11px;
      }
      .re-send-email:focus, .edit:focus {
        outline: none;
        box-shadow: 0px 0px 10px #0a21f3a3;
      }
      .el-select .el-input {
        width: 151px;
        .el-input__inner {
          padding-right: 0px;
          color: var(--site-font-primary-color);
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
          padding-right: 10px;
        }
      }
      .el-select .el-input .el-select__caret {
        background-image: url('../../assets/icons/icon_dropdown_arrow_down.png');
        background-repeat: no-repeat;
        background-size: 8px 5px;
        background-position-x: 4px;
        background-position-y: 12px;
        width: 20px;
        height: 20px;
        vertical-align: middle;
        transform: rotateZ(0);
      }
      .el-icon-arrow-up:before {
        content: " ";
      }
      .el-button  {
        padding-bottom: 6.5px;
      }
    }
  } 
}

.wys-dialog-box.user-steps-dialog {  
  .el-dialog {
    height: 500px;  
    padding: 10px 0px 0px 0px;
    .el-dialog__body {
        padding: 10px 0px 10px 0px;
        color: var(--site-font-primary-color);
    }
  }
  .el-dialog__header {
    display: none;
  }
}

.wys-dialog-box.user-eidt-dialog {  
  .el-dialog {
    height: 78%; 
    min-height: 765px !important;
    padding: 10px 0px 0px 0px;
    .el-dialog__body {
      padding: 10px 0px 10px 0px;
      color: var(--site-font-primary-color);
      height: 100%;
    }
  }
  .el-dialog__header {
    display: none;
  }
}

// .wy-management-team .user-list {
//     width: -webkit-fit-content;
//     width: -moz-fit-content;
//     width: fit-content;
//     padding-right: 20px;
//     overflow-y: auto;
//     height: 100%;
//     /* height: calc(100% - 769px); */
//     height: -webkit-fill-available;
// }
</style>